/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class MultiStepForm extends HTMLElement {
  constructor() {
    super();
  }
}

class APIRequestHandler extends HTMLFormElement {
  constructor() {
    super();
    this.inputItems = this.querySelectorAll("[data-input]");
    this.baseURL = "https://lostandfound-backend-blue-lake-4432.fly.dev/api";
    this.headers = {
      "x-api-key": "68a33c1b37216df690c7612fb2c0d1f3",
      "Content-Type": "application/json",
    };
  }

  _collectData() {
    const data = this.data || {};
    this.inputItems.forEach((el) => {
      const key = el.name || el.dataset.input;
      if (!key) return;
      data[key] = el.value;
    });
    return data;
  }

  async makeRequest(method, endpoint, callBack) {
    const data = this._collectData();
    let url = this.baseURL + endpoint;
    let options = { method, headers: this.headers };

    if (method.toUpperCase() !== "GET") {
      options.body = JSON.stringify(data);
    }

    try {
      const res = await fetch(url, options);
      const json = await res.json();
      if (callBack) callBack(json, res);
    } catch (err) {
      console.error("API error:", err);
      if (callBack) callBack(null, err);
    }
  }
}

customElements.define("api-request-handler", APIRequestHandler, {
  extends: "form",
});

const ACTIVATE_CODE_COUNTRIES = [
  { code: "AL", name: "Albania", dialCode: "+355" },
  { code: "AD", name: "Andorra", dialCode: "+376" },
  { code: "AM", name: "Armenia", dialCode: "+374" },
  { code: "AT", name: "Austria", dialCode: "+43" },
  { code: "AZ", name: "Azerbaijan", dialCode: "+994" },
  { code: "BY", name: "Belarus", dialCode: "+375" },
  { code: "BE", name: "Belgium", dialCode: "+32" },
  { code: "BA", name: "Bosnia and Herzegovina", dialCode: "+387" },
  { code: "BG", name: "Bulgaria", dialCode: "+359" },
  { code: "HR", name: "Croatia", dialCode: "+385" },
  { code: "CY", name: "Cyprus", dialCode: "+357" },
  { code: "CZ", name: "Czech Republic", dialCode: "+420" },
  { code: "DK", name: "Denmark", dialCode: "+45" },
  { code: "EE", name: "Estonia", dialCode: "+372" },
  { code: "FI", name: "Finland", dialCode: "+358" },
  { code: "FR", name: "France", dialCode: "+33" },
  { code: "GE", name: "Georgia", dialCode: "+995" },
  { code: "DE", name: "Germany", dialCode: "+49" },
  { code: "GR", name: "Greece", dialCode: "+30" },
  { code: "HU", name: "Hungary", dialCode: "+36" },
  { code: "IS", name: "Iceland", dialCode: "+354" },
  { code: "IE", name: "Ireland", dialCode: "+353" },
  { code: "IT", name: "Italy", dialCode: "+39" },
  { code: "XK", name: "Kosovo", dialCode: "+383" },
  { code: "LV", name: "Latvia", dialCode: "+371" },
  { code: "LI", name: "Liechtenstein", dialCode: "+423" },
  { code: "LT", name: "Lithuania", dialCode: "+370" },
  { code: "LU", name: "Luxembourg", dialCode: "+352" },
  { code: "MT", name: "Malta", dialCode: "+356" },
  { code: "MD", name: "Moldova", dialCode: "+373" },
  { code: "MC", name: "Monaco", dialCode: "+377" },
  { code: "ME", name: "Montenegro", dialCode: "+382" },
  { code: "NL", name: "Netherlands", dialCode: "+31" },
  { code: "MK", name: "North Macedonia", dialCode: "+389" },
  { code: "NO", name: "Norway", dialCode: "+47" },
  { code: "PL", name: "Poland", dialCode: "+48" },
  { code: "PT", name: "Portugal", dialCode: "+351" },
  { code: "RO", name: "Romania", dialCode: "+40" },
  { code: "SM", name: "San Marino", dialCode: "+378" },
  { code: "RS", name: "Serbia", dialCode: "+381" },
  { code: "SK", name: "Slovakia", dialCode: "+421" },
  { code: "SI", name: "Slovenia", dialCode: "+386" },
  { code: "ES", name: "Spain", dialCode: "+34" },
  { code: "SE", name: "Sweden", dialCode: "+46" },
  { code: "CH", name: "Switzerland", dialCode: "+41" },
  { code: "TR", name: "Turkey", dialCode: "+90" },
  { code: "UA", name: "Ukraine", dialCode: "+380" },
  { code: "GB", name: "United Kingdom", dialCode: "+44" },
  { code: "VA", name: "Vatican City", dialCode: "+379" },
  { code: "AF", name: "Afghanistan", dialCode: "+93" },
  { code: "DZ", name: "Algeria", dialCode: "+213" },
  { code: "AO", name: "Angola", dialCode: "+244" },
  { code: "AG", name: "Antigua and Barbuda", dialCode: "+1-268" },
  { code: "AR", name: "Argentina", dialCode: "+54" },
  { code: "AU", name: "Australia", dialCode: "+61" },
  { code: "BS", name: "Bahamas", dialCode: "+1-242" },
  { code: "BH", name: "Bahrain", dialCode: "+973" },
  { code: "BD", name: "Bangladesh", dialCode: "+880" },
  { code: "BB", name: "Barbados", dialCode: "+1-246" },
  { code: "BZ", name: "Belize", dialCode: "+501" },
  { code: "BJ", name: "Benin", dialCode: "+229" },
  { code: "BT", name: "Bhutan", dialCode: "+975" },
  { code: "BO", name: "Bolivia", dialCode: "+591" },
  { code: "BW", name: "Botswana", dialCode: "+267" },
  { code: "BR", name: "Brazil", dialCode: "+55" },
  { code: "BN", name: "Brunei", dialCode: "+673" },
  { code: "BF", name: "Burkina Faso", dialCode: "+226" },
  { code: "BI", name: "Burundi", dialCode: "+257" },
  { code: "CV", name: "Cabo Verde", dialCode: "+238" },
  { code: "KH", name: "Cambodia", dialCode: "+855" },
  { code: "CM", name: "Cameroon", dialCode: "+237" },
  { code: "CA", name: "Canada", dialCode: "+1" },
  { code: "CF", name: "Central African Republic", dialCode: "+236" },
  { code: "TD", name: "Chad", dialCode: "+235" },
  { code: "CL", name: "Chile", dialCode: "+56" },
  { code: "CN", name: "China", dialCode: "+86" },
  { code: "CO", name: "Colombia", dialCode: "+57" },
  { code: "KM", name: "Comoros", dialCode: "+269" },
  { code: "CG", name: "Congo", dialCode: "+242" },
  { code: "CD", name: "Congo (DRC)", dialCode: "+243" },
  { code: "CR", name: "Costa Rica", dialCode: "+506" },
  { code: "CI", name: "Cote d'Ivoire", dialCode: "+225" },
  { code: "CU", name: "Cuba", dialCode: "+53" },
  { code: "DJ", name: "Djibouti", dialCode: "+253" },
  { code: "DM", name: "Dominica", dialCode: "+1-767" },
  { code: "DO", name: "Dominican Republic", dialCode: "+1-809" },
  { code: "EC", name: "Ecuador", dialCode: "+593" },
  { code: "EG", name: "Egypt", dialCode: "+20" },
  { code: "SV", name: "El Salvador", dialCode: "+503" },
  { code: "GQ", name: "Equatorial Guinea", dialCode: "+240" },
  { code: "ER", name: "Eritrea", dialCode: "+291" },
  { code: "SZ", name: "Eswatini", dialCode: "+268" },
  { code: "ET", name: "Ethiopia", dialCode: "+251" },
  { code: "FJ", name: "Fiji", dialCode: "+679" },
  { code: "GA", name: "Gabon", dialCode: "+241" },
  { code: "GM", name: "Gambia", dialCode: "+220" },
  { code: "GH", name: "Ghana", dialCode: "+233" },
  { code: "GD", name: "Grenada", dialCode: "+1-473" },
  { code: "GT", name: "Guatemala", dialCode: "+502" },
  { code: "GN", name: "Guinea", dialCode: "+224" },
  { code: "GW", name: "Guinea-Bissau", dialCode: "+245" },
  { code: "GY", name: "Guyana", dialCode: "+592" },
  { code: "HT", name: "Haiti", dialCode: "+509" },
  { code: "HN", name: "Honduras", dialCode: "+504" },
  { code: "IN", name: "India", dialCode: "+91" },
  { code: "ID", name: "Indonesia", dialCode: "+62" },
  { code: "IR", name: "Iran", dialCode: "+98" },
  { code: "IQ", name: "Iraq", dialCode: "+964" },
  { code: "IL", name: "Israel", dialCode: "+972" },
  { code: "JM", name: "Jamaica", dialCode: "+1-876" },
  { code: "JP", name: "Japan", dialCode: "+81" },
  { code: "JO", name: "Jordan", dialCode: "+962" },
  { code: "KZ", name: "Kazakhstan", dialCode: "+7" },
  { code: "KE", name: "Kenya", dialCode: "+254" },
  { code: "KI", name: "Kiribati", dialCode: "+686" },
  { code: "KW", name: "Kuwait", dialCode: "+965" },
  { code: "KG", name: "Kyrgyzstan", dialCode: "+996" },
  { code: "LA", name: "Laos", dialCode: "+856" },
  { code: "LB", name: "Lebanon", dialCode: "+961" },
  { code: "LS", name: "Lesotho", dialCode: "+266" },
  { code: "LR", name: "Liberia", dialCode: "+231" },
  { code: "LY", name: "Libya", dialCode: "+218" },
  { code: "MG", name: "Madagascar", dialCode: "+261" },
  { code: "MW", name: "Malawi", dialCode: "+265" },
  { code: "MY", name: "Malaysia", dialCode: "+60" },
  { code: "MV", name: "Maldives", dialCode: "+960" },
  { code: "ML", name: "Mali", dialCode: "+223" },
  { code: "MH", name: "Marshall Islands", dialCode: "+692" },
  { code: "MR", name: "Mauritania", dialCode: "+222" },
  { code: "MU", name: "Mauritius", dialCode: "+230" },
  { code: "MX", name: "Mexico", dialCode: "+52" },
  { code: "FM", name: "Micronesia", dialCode: "+691" },
  { code: "MN", name: "Mongolia", dialCode: "+976" },
  { code: "MA", name: "Morocco", dialCode: "+212" },
  { code: "MZ", name: "Mozambique", dialCode: "+258" },
  { code: "MM", name: "Myanmar", dialCode: "+95" },
  { code: "NA", name: "Namibia", dialCode: "+264" },
  { code: "NR", name: "Nauru", dialCode: "+674" },
  { code: "NP", name: "Nepal", dialCode: "+977" },
  { code: "NZ", name: "New Zealand", dialCode: "+64" },
  { code: "NI", name: "Nicaragua", dialCode: "+505" },
  { code: "NE", name: "Niger", dialCode: "+227" },
  { code: "NG", name: "Nigeria", dialCode: "+234" },
  { code: "KP", name: "North Korea", dialCode: "+850" },
  { code: "OM", name: "Oman", dialCode: "+968" },
  { code: "PK", name: "Pakistan", dialCode: "+92" },
  { code: "PW", name: "Palau", dialCode: "+680" },
  { code: "PS", name: "Palestine", dialCode: "+970" },
  { code: "PA", name: "Panama", dialCode: "+507" },
  { code: "PG", name: "Papua New Guinea", dialCode: "+675" },
  { code: "PY", name: "Paraguay", dialCode: "+595" },
  { code: "PE", name: "Peru", dialCode: "+51" },
  { code: "PH", name: "Philippines", dialCode: "+63" },
  { code: "QA", name: "Qatar", dialCode: "+974" },
  { code: "RW", name: "Rwanda", dialCode: "+250" },
  { code: "KN", name: "Saint Kitts and Nevis", dialCode: "+1-869" },
  { code: "LC", name: "Saint Lucia", dialCode: "+1-758" },
  { code: "VC", name: "Saint Vincent and the Grenadines", dialCode: "+1-784" },
  { code: "WS", name: "Samoa", dialCode: "+685" },
  { code: "ST", name: "Sao Tome and Principe", dialCode: "+239" },
  { code: "SA", name: "Saudi Arabia", dialCode: "+966" },
  { code: "SN", name: "Senegal", dialCode: "+221" },
  { code: "SC", name: "Seychelles", dialCode: "+248" },
  { code: "SL", name: "Sierra Leone", dialCode: "+232" },
  { code: "SG", name: "Singapore", dialCode: "+65" },
  { code: "SB", name: "Solomon Islands", dialCode: "+677" },
  { code: "SO", name: "Somalia", dialCode: "+252" },
  { code: "ZA", name: "South Africa", dialCode: "+27" },
  { code: "KR", name: "South Korea", dialCode: "+82" },
  { code: "SS", name: "South Sudan", dialCode: "+211" },
  { code: "LK", name: "Sri Lanka", dialCode: "+94" },
  { code: "SD", name: "Sudan", dialCode: "+249" },
  { code: "SR", name: "Suriname", dialCode: "+597" },
  { code: "SY", name: "Syria", dialCode: "+963" },
  { code: "TW", name: "Taiwan", dialCode: "+886" },
  { code: "TJ", name: "Tajikistan", dialCode: "+992" },
  { code: "TZ", name: "Tanzania", dialCode: "+255" },
  { code: "TH", name: "Thailand", dialCode: "+66" },
  { code: "TL", name: "Timor-Leste", dialCode: "+670" },
  { code: "TG", name: "Togo", dialCode: "+228" },
  { code: "TO", name: "Tonga", dialCode: "+676" },
  { code: "TT", name: "Trinidad and Tobago", dialCode: "+1-868" },
  { code: "TN", name: "Tunisia", dialCode: "+216" },
  { code: "TM", name: "Turkmenistan", dialCode: "+993" },
  { code: "TV", name: "Tuvalu", dialCode: "+688" },
  { code: "UG", name: "Uganda", dialCode: "+256" },
  { code: "AE", name: "United Arab Emirates", dialCode: "+971" },
  { code: "US", name: "United States", dialCode: "+1" },
  { code: "UY", name: "Uruguay", dialCode: "+598" },
  { code: "UZ", name: "Uzbekistan", dialCode: "+998" },
  { code: "VU", name: "Vanuatu", dialCode: "+678" },
  { code: "VE", name: "Venezuela", dialCode: "+58" },
  { code: "VN", name: "Vietnam", dialCode: "+84" },
  { code: "YE", name: "Yemen", dialCode: "+967" },
  { code: "ZM", name: "Zambia", dialCode: "+260" },
  { code: "ZW", name: "Zimbabwe", dialCode: "+263" },
];

const PRIORITY_COUNTRY_CODES = ["DE", "AT", "CH", "IT", "FR"];
const PRIORITY_COUNTRY_INDEX = new Map(
  PRIORITY_COUNTRY_CODES.map((countryCode, index) => [countryCode, index])
);

const COUNTRY_NAME_COLLATOR = new Intl.Collator("en", { sensitivity: "base" });

const SORTED_ACTIVATE_CODE_COUNTRIES = [...ACTIVATE_CODE_COUNTRIES].sort(
  (left, right) => {
    const leftPriority = PRIORITY_COUNTRY_INDEX.get(left.code);
    const rightPriority = PRIORITY_COUNTRY_INDEX.get(right.code);
    const leftHasPriority = leftPriority !== undefined;
    const rightHasPriority = rightPriority !== undefined;

    if (leftHasPriority && rightHasPriority) {
      return leftPriority - rightPriority;
    }

    if (leftHasPriority) {
      return -1;
    }

    if (rightHasPriority) {
      return 1;
    }

    return COUNTRY_NAME_COLLATOR.compare(left.name, right.name);
  }
);

function getCountryFlagEmoji(countryCode) {
  if (!countryCode || countryCode.length !== 2) {
    return "";
  }

  return countryCode
    .toUpperCase()
    .replace(/./g, (character) =>
      String.fromCodePoint(127397 + character.charCodeAt(0))
    );
}

class FoundCodeHandler extends APIRequestHandler {
  constructor() {
    super();
    this.currentCode = null;
    this.section = this.closest("[data-section-id]");
  }

  connectedCallback() {
    this.addEventListener("submit", this.handleVerification.bind(this));
    this.initializeRetryButton();
    this.initializeCodeUppercase();
    this.prefillFromUrl();
  }

  initializeCodeUppercase() {
    const input = this.querySelector('[data-input="code"]');
    if (!input) return;

    input.addEventListener("input", () => {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      input.value = input.value.toUpperCase();
      input.setSelectionRange(start, end);
    });
  }

  prefillFromUrl() {
    const codeParam = new URLSearchParams(window.location.search).get("code");
    if (!codeParam) return;

    const input = this.querySelector('[data-input="code"]');
    if (!input) return;

    const normalized = codeParam.trim().toUpperCase();
    input.value = normalized;

    if (/^[A-Z0-9]{8}$/.test(normalized)) {
      this.requestSubmit();
    }
  }

  handleVerification(e) {
    e.preventDefault();

    const submitButton = this.querySelector('[data-button="verify"]');
    const loadingText = submitButton.dataset.textLoading || "Loading...";
    const originalText = submitButton.textContent;
    const errorDiv = this.section.querySelector('[data-message="error"]');

    errorDiv.style.display = "none";
    submitButton.textContent = loadingText;
    submitButton.disabled = true;

    this.makeRequest(
      "GET",
      "/foundCode/checkActiveCode/singlecode",
      (data, res) => {
        submitButton.textContent = originalText;
        submitButton.disabled = false;
        this.handleVerificationResponse(data, res);
      }
    );
  }

  async makeRequest(method, endpoint, callBack) {
    const data = this._collectData();
    let url = this.baseURL + endpoint;

    if (endpoint.includes("checkActiveCode/singlecode")) {
      url =
        this.baseURL +
        `/foundCode/checkActiveCode/${encodeURIComponent(data.code)}`;
    }

    let options = { method, headers: this.headers };

    if (
      method.toUpperCase() !== "GET" &&
      !endpoint.includes("checkActiveCode")
    ) {
      options.body = JSON.stringify(data);
    }

    try {
      const res = await fetch(url, options);
      const json = await res.json();
      if (callBack) callBack(json, res);
    } catch (err) {
      console.error("API error:", err);
      if (callBack) callBack(null, err);
    }
  }

  handleVerificationResponse(data, res) {
    const errorDiv = this.section.querySelector('[data-message="error"]');
    const successDiv = this.section.querySelector('[data-step="2"]');
    const formWrapper = this.section.querySelector('[data-step="1"]');

    if (data && data.exists && data.valid) {
      this.currentCode = this._collectData().code;
      this.showSuccessState();
    } else {
      this.showErrorState();
    }
  }

  showSuccessState() {
    const formWrapper = this.section.querySelector('[data-step="1"]');
    const successDiv = this.section.querySelector('[data-step="2"]');
    const codeHeader = this.section.querySelector(".found-code-header");

    formWrapper.style.display = "none";
    successDiv.style.display = "block";
    codeHeader.style.display = "none";
    this.initializeSuccessHandlers();
  }

  showErrorState() {
    const errorDiv = this.section.querySelector('[data-message="error"]');
    const input = this.querySelector('[data-input="code"]');

    errorDiv.style.display = "block";
    input.classList.add("error-state");
    input.value = "";
  }

  initializeRetryButton() {
    const retryBtn = this.section.querySelector('[data-action="retry"]');
    if (retryBtn) {
      retryBtn.addEventListener("click", () => {
        const errorDiv = this.section.querySelector('[data-message="error"]');
        const input = this.querySelector('[data-input="code"]');

        errorDiv.style.display = "none";
        input.classList.remove("error-state");
        input.focus();
      });
    }
  }

  initializeSuccessHandlers() {
    const contactToggle = this.section.querySelector(
      '[data-action="show-contact-form"]'
    );
    if (contactToggle) {
      contactToggle.addEventListener("click", () => {
        this.showContactForm();
      });
    }

    const mailToggle = this.section.querySelector(
      '[data-action="show-mail-info"]'
    );
    if (mailToggle) {
      mailToggle.addEventListener("click", () => {
        this.toggleMailInfo();
      });
    }
  }

  showContactForm() {
    const contactDiv = this.section.querySelector('[data-step="3"]');
    contactDiv.hasAttribute("hidden")
      ? contactDiv.removeAttribute("hidden")
      : contactDiv.setAttribute("hidden", true);
    this.section
      .querySelector(`[data-action="show-contact-form"]`)
      ?.classList.toggle("btn-outline");
  }

  toggleMailInfo() {
    const mailInfo = this.section.querySelector('[data-info="mail"]');
    const toggle = this.section.querySelector(
      '[data-action="show-mail-info"] .toggle-icon'
    );

    if (mailInfo.hasAttribute("hidden")) {
      mailInfo.removeAttribute("hidden");
      if (toggle) toggle.style.transform = "rotate(180deg)";
    } else {
      mailInfo.setAttribute("hidden", true);
      if (toggle) toggle.style.transform = "rotate(0deg)";
    }
    this.section
      .querySelector('[data-action="show-mail-info"]')
      .classList.toggle("btn-outline");
  }
}

customElements.define("found-code-handler", FoundCodeHandler, {
  extends: "form",
});

class ActivateCodeHandler extends APIRequestHandler {
  constructor() {
    super();
    this.currentCode = null;
    this.section = this.closest("[data-section-id]");
  }

  connectedCallback() {
    this.addEventListener("submit", this.handleVerification.bind(this));
    this.initializeRetryButton();
  }

  async handleVerification(e) {
    e.preventDefault();

    const submitButton = this.querySelector('[data-button="verify"]');
    const loadingText = submitButton.dataset.textLoading || "Loading...";
    const originalText = submitButton.textContent;

    submitButton.textContent = loadingText;
    submitButton.disabled = true;

    const data = this._collectData();
    const code = data.code;

    try {
      let res = await fetch(
        `${this.baseURL}/foundCode/checkActiveCode/${encodeURIComponent(code)}`,
        {
          method: "GET",
          headers: this.headers,
        }
      );

      let json = await res.json();
      const exists = Boolean(json?.exists);
      const valid = Boolean(json?.valid);

      if (!exists) {
        this.errorMessage = this.getAttribute("data-error-text");
        this.currentCode = code;
        this.showErrorState();
      } else if (valid) {
        this.errorMessage = this.getAttribute(
          "data-error-code-already-active-text"
        );
        this.currentCode = code;
        this.showErrorState();
      } else {
        this.currentCode = code;
        this.showSuccessState();
      }
    } catch (err) {
      console.error("API error:", err);
      this.showErrorState();
    } finally {
      submitButton.textContent = originalText;
      submitButton.disabled = false;
    }
  }

  handleVerificationResponse(data, res) {
    const errorDiv = this.section.querySelector('[data-message="error"]');
    const successDiv = this.section.querySelector('[data-step="2"]');
    const formWrapper = this.section.querySelector('[data-step="1"]');

    if (data && data.exists && data.valid) {
      this.currentCode = this._collectData().code;
      this.showSuccessState();
    } else {
      this.showErrorState();
    }
  }

  showSuccessState() {
    const formWrapper = this.section.querySelector('[data-step="1"]');
    const successDiv = this.section.querySelector('[data-step="2"]');
    const successHeader = successDiv.querySelector(".success-header");

    formWrapper.style.display = "none";
    successDiv.style.display = "block";

    const originalInput = this.querySelector('[data-input="code"]');
    if (originalInput) {
      const clonedInput = originalInput.cloneNode(true);

      clonedInput.value = this.currentCode;
      clonedInput.setAttribute("readonly", "true");

      const wrapper = document.createElement("div");
      wrapper.className = "input-group";
      wrapper.appendChild(clonedInput);

      successHeader.appendChild(wrapper);
    }
    this.initializeSuccessHandlers();
  }

  showErrorState() {
    const errorDiv = this.section.querySelector('[data-message="error"]');
    const input = this.querySelector('[data-input="code"]');

    errorDiv.querySelector("p").textContent = this.errorMessage;

    errorDiv.style.display = "block";
    input.classList.add("error-state");
  }

  initializeRetryButton() {
    const retryBtn = this.section.querySelector('[data-action="retry"]');
    if (retryBtn) {
      retryBtn.addEventListener("click", () => {
        const errorDiv = this.section.querySelector('[data-message="error"]');
        const input = this.querySelector('[data-input="code"]');

        errorDiv.style.display = "none";
        input.classList.remove("error-state");
        input.focus();
      });
    }
  }

  initializeSuccessHandlers() {
    const contactToggle = this.section.querySelector(
      '[data-action="show-contact-form"]'
    );
    if (contactToggle) {
      contactToggle.addEventListener("click", () => {
        this.showContactForm();
      });
    }

    const mailToggle = this.section.querySelector(
      '[data-action="show-mail-info"]'
    );
    if (mailToggle) {
      mailToggle.addEventListener("click", () => {
        this.toggleMailInfo();
      });
    }
  }

  showContactForm() {
    const successDiv = this.section.querySelector('[data-step="2"]');
    const contactDiv = this.section.querySelector('[data-step="3"]');

    successDiv.style.display = "none";
    contactDiv.style.display = "block";
  }

  toggleMailInfo() {
    const mailInfo = this.section.querySelector('[data-info="mail"]');
    const toggle = this.section.querySelector(
      '[data-action="show-mail-info"] .toggle-icon'
    );

    if (mailInfo.style.display === "none" || mailInfo.style.display === "") {
      mailInfo.style.display = "block";
      if (toggle) toggle.style.transform = "rotate(180deg)";
    } else {
      mailInfo.style.display = "none";
      if (toggle) toggle.style.transform = "rotate(0deg)";
    }
  }
}

customElements.define("activate-code-handler", ActivateCodeHandler, {
  extends: "form",
});

window.customElements.define("multi-step-form", MultiStepForm);

if (!customElements.get("found-code-contact-handler")) {
  customElements.define(
    "found-code-contact-handler",
    class FoundCodeContactHandler extends APIRequestHandler {
      constructor() {
        super();
        this.currentLocation = null;
        this.section = this.closest("[data-section-id]");
        this.data = {};
      }

      connectedCallback() {
        this.addEventListener("submit", this.handleContactSubmit.bind(this));
        this.initializeLocationSharing();
        this.initializeLocationRemoving();
      }

      handleContactSubmit(e) {
        e.preventDefault();

        const submitButton = this.querySelector(".contact-submit");
        const sendingText = submitButton.dataset.textSending || "Sending...";
        const originalText = submitButton.textContent;

        submitButton.textContent = sendingText;
        submitButton.disabled = true;

        if (!this.section) {
          console.error("Section not found for contact submit");
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          return;
        }

        const codeHandler = this.section.querySelector(
          "[is='found-code-handler']"
        );
        const codeInput = codeHandler
          ? codeHandler.querySelector('[data-input="code"]')
          : null;
        const code = codeInput ? codeInput.value : "";

        if (!code) {
          console.error("Code not found for contact submit");
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          return;
        }

        const hiddenCodeInput = document.createElement("input");
        hiddenCodeInput.type = "hidden";
        hiddenCodeInput.setAttribute("data-input", "code");
        hiddenCodeInput.value = code;
        this.data.code = code;

        this.makeRequest("POST", "/foundCode", (data, res) => {
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          this.handleContactResponse(data, res);
        });
      }

      handleContactResponse(data, res) {
        if (data && data.success) {
          this.showFinalSuccess();
        } else {
          this.showContactError(data);
        }
      }

      showFinalSuccess() {
        if (!this.section) return;
        const formDiv = this.section.querySelector('[data-step="2"]');
        const contactDiv = this.section.querySelector('[data-step="3"]');
        const finalSuccessDiv = this.section.querySelector('[data-step="4"]');
        const header = this.section.querySelector(".found-code-header");
        const mailInfo = this.section.querySelector(".mail-info");
        if (formDiv) formDiv.style.display = "none";
        if (contactDiv) contactDiv.style.display = "none";
        if (finalSuccessDiv) finalSuccessDiv.style.display = "block";
        if (header) header.style.display = "none";
        if (mailInfo) mailInfo.style.display = "none";

        if (finalSuccessDiv)
          finalSuccessDiv.scrollIntoView({ behavior: "smooth" });
      }

      showContactError(data) {
        let errorDiv = this.querySelector(".contact-error");
        const errorMessage =
          this.dataset.textError || "An error occurred. Please try again.";

        if (!errorDiv) {
          errorDiv = document.createElement("div");
          errorDiv.className = "contact-error error-message";
          errorDiv.innerHTML = `<p>${errorMessage}</p>`;
          this.appendChild(errorDiv);
        }

        errorDiv.style.display = "block";
        setTimeout(() => (errorDiv.style.display = "none"), 5000);
      }

      initializeLocationSharing() {
        const locationBtn = this.querySelector(
          '[data-action="share-location"]'
        );
        const removingBtn = this.querySelector(
          '[data-action="remove-location"]'
        );
        const locationInput = this.querySelector('[data-input="location"]');

        if (locationBtn) {
          locationBtn.addEventListener("click", () => {
            this.requestLocation(locationBtn, removingBtn, locationInput);
          });
        }
      }

      initializeLocationRemoving() {
        const locationBtn = this.querySelector(
          '[data-action="share-location"]'
        );
        const removingBtn = this.querySelector(
          '[data-action="remove-location"]'
        );
        const locationInput = this.querySelector('[data-input="location"]');

        if (removingBtn) {
          removingBtn.addEventListener("click", () => {
            console.log("jer==");

            this.resetLocation(locationBtn, removingBtn, locationInput);
          });
        }
      }

      resetLocation(button, removeBtn, input) {
        button.classList.remove("location-shared");
        removeBtn.classList.remove("location-shared");
        input.value = "";
      }

      requestLocation(button, removeBtn, input) {
        const originalText = button.innerHTML;

        if (!navigator.geolocation) {
          alert("Geolocation wird von diesem Browser nicht unterstützt.");
          return;
        }

        button.classList.add("loading-state");
        button.disabled = true;

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const locationString = `${latitude},${longitude}`;

            input.value = locationString;
            this.currentLocation = locationString;

            button.classList.remove("loading-state");
            button.classList.add("location-shared");
            removeBtn.classList.add("location-shared");
            button.disabled = false;
            button.classList.add("location-shared");
          },
          (error) => {
            console.error("Error getting location:", error);
            button.innerHTML = originalText;
            button.disabled = false;

            let errorMessage = "Standort konnte nicht ermittelt werden.";
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = "Standortzugriff wurde verweigert.";
                break;
              case error.POSITION_UNAVAILABLE:
                errorMessage = "Standortinformationen sind nicht verfügbar.";
                break;
              case error.TIMEOUT:
                errorMessage = "Standortabfrage ist abgelaufen.";
                break;
            }

            alert(errorMessage);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000,
          }
        );
      }
    },
    { extends: "form" }
  );
}

if (!customElements.get("activate-code-contact-handler")) {
  customElements.define(
    "activate-code-contact-handler",
    class ActivateCodeContactHandler extends APIRequestHandler {
      constructor() {
        super();
        this.currentLocation = null;
        this.section = this.closest("[data-section-id]");
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener("submit", this.handleContactSubmit.bind(this));
        this.initializeCountrySelector();
        this.initializePhoneNumberInput();
      }

      disconnectedCallback() {
        document.removeEventListener("click", this.handleDocumentClick);
      }

      async handleContactSubmit(e) {
        e.preventDefault();

        const submitButton = this.querySelector(".contact-submit");
        const sendingText = submitButton.dataset.textSending || "Sending...";
        const originalText = submitButton.textContent;

        submitButton.textContent = sendingText;
        submitButton.disabled = true;

        if (!this.section) {
          console.error("Section not found for contact submit");
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          return;
        }

        // get the code from success step
        const codeHandler = this.section.querySelector(
          ".activate-code-success"
        );
        const codeInput = codeHandler
          ? codeHandler.querySelector('[data-input="code"]')
          : null;
        const code = codeInput ? codeInput.value : "";

        if (!code) {
          console.error("Code not found for contact submit");
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          return;
        }

        if (!this.validateCountrySelection()) {
          submitButton.textContent = originalText;
          submitButton.disabled = false;
          return;
        }

        if (this.phoneNumberInput) {
          this.phoneNumberInput.value = this.phoneNumberInput.value.replace(
            /\D+/g,
            ""
          );

          if (!this.phoneNumberInput.reportValidity()) {
            submitButton.textContent = originalText;
            submitButton.disabled = false;
            return;
          }
        }

        try {
          const formData = this._collectData();
          const fullName = formData.finderName
            ? formData.finderName.trim()
            : "";

          // if (fullName) {
          //   const parts = fullName.split(" ");
          //   firstName = parts.shift();
          //   lastName = parts.join(" ") || "";
          // }

          const countryCode = (formData.finderPhoneCountry || "")
            .trim()
            .replace(/(?!^\+)\D+/g, "");
          const phoneNumber = (formData.finderPhoneNumber || "")
            .trim()
            .replace(/\D+/g, "");
          const phone = `${countryCode}${phoneNumber}`;

          const payload = {
            code,
            lastName: fullName,
            email: formData.finderEmail || "",
            phone,
            city: formData.findCity || "",
            street: formData.findStreet || "",
            zipCode: formData.findZipCode || "",
            message: formData.message || "",
            language: formData.language || "",
          };
          payload.language = new FormData(this).get("language");

          const res = await fetch(`${this.baseURL}/userdata`, {
            method: "POST",
            headers: this.headers,
            body: JSON.stringify(payload),
          });

          const json = await res.json();

          if (res.ok) {
            this.handleContactResponse(json, res);
          } else {
            this.showContactError(json);
          }
        } catch (err) {
          console.error("API error:", err);
          this.showContactError();
        } finally {
          submitButton.textContent = originalText;
          submitButton.disabled = false;
        }
      }

      handleContactResponse(data, res) {
        if (data) {
          this.showFinalSuccess();
        } else {
          this.showContactError(data);
        }
      }
      showFinalSuccess() {
        if (!this.section) return;

        const contactDiv = this.section.querySelector('[data-step="2"]');
        const finalSuccessDiv = this.section.querySelector('[data-step="3"]');
        const activateContentHeader = this.section.querySelector(
          ".activate-code-header"
        );

        if (contactDiv) contactDiv.style.display = "none";
        if (activateContentHeader) activateContentHeader.style.display = "none";
        if (finalSuccessDiv) finalSuccessDiv.style.display = "block";

        if (finalSuccessDiv)
          finalSuccessDiv.scrollIntoView({ behavior: "smooth" });
      }

      showContactError(data) {
        let errorDiv = this.querySelector(".contact-error");
        if (!errorDiv) {
          errorDiv = document.createElement("div");
          errorDiv.className = "contact-error error-message";
          errorDiv.innerHTML =
            "<p>Es gab einen Fehler beim Senden deiner Nachricht. Bitte versuche es erneut.</p>";
          this.appendChild(errorDiv);
        }

        errorDiv.style.display = "block";

        setTimeout(() => {
          errorDiv.style.display = "none";
        }, 5000);
      }

      initializePhoneNumberInput() {
        this.phoneNumberInput = this.querySelector(
          '[data-input="finderPhoneNumber"]'
        );
        if (!this.phoneNumberInput || this.phoneNumberInput.dataset.initialized) {
          return;
        }

        this.phoneNumberInput.dataset.initialized = "true";
        this.phoneNumberInput.addEventListener("input", () => {
          const digitsOnly = this.phoneNumberInput.value.replace(/\D+/g, "");
          if (this.phoneNumberInput.value !== digitsOnly) {
            this.phoneNumberInput.value = digitsOnly;
          }
        });
      }

      initializeCountrySelector() {
        this.countrySelector = this.querySelector("[data-country-selector]");
        if (!this.countrySelector || this.countrySelector.dataset.initialized) {
          return;
        }

        this.countrySelector.dataset.initialized = "true";
        this.countryInput = this.querySelector('[data-input="finderPhoneCountry"]');
        this.countryTrigger = this.querySelector(
          '[data-action="toggle-country-selector"]'
        );
        this.countryTriggerPlaceholder = this.querySelector(
          ".country-selector__trigger-placeholder"
        );
        this.countryTriggerValue = this.querySelector(
          ".country-selector__trigger-value"
        );
        this.countryDropdown = this.querySelector("[data-country-dropdown]");
        this.countryOptions = this.querySelector("[data-country-options]");

        if (
          !this.countryInput ||
          !this.countryTrigger ||
          !this.countryTriggerPlaceholder ||
          !this.countryTriggerValue ||
          !this.countryDropdown ||
          !this.countryOptions
        ) {
          return;
        }

        this.renderCountryOptions();
        this.updateCountryTrigger();

        this.countryTrigger.addEventListener("click", () => {
          this.toggleCountryDropdown();
        });

        this.countryOptions.addEventListener("click", (event) => {
          const option = event.target.closest("[data-country-code]");
          if (!option) return;

          const selectedCountry = SORTED_ACTIVATE_CODE_COUNTRIES.find(
            ({ code }) => code === option.dataset.countryCode
          );

          if (selectedCountry) {
            this.selectCountry(selectedCountry);
          }
        });

        this.countryTrigger.addEventListener("keydown", (event) => {
          if (event.key === "Escape") {
            this.closeCountryDropdown();
          }
        });

        document.addEventListener("click", this.handleDocumentClick);
      }

      handleDocumentClick(event) {
        if (!this.countrySelector || this.countryDropdown.hidden) {
          return;
        }

        if (!this.countrySelector.contains(event.target)) {
          this.closeCountryDropdown();
        }
      }

      renderCountryOptions() {
        this.countryOptions.innerHTML = SORTED_ACTIVATE_CODE_COUNTRIES.map(
          (country) => {
            const flag = getCountryFlagEmoji(country.code);

            return `
              <li>
                <button
                  type="button"
                  class="country-selector__option"
                  data-country-code="${country.code}"
                  aria-selected="false"
                >
                  <span class="country-selector__flag" aria-hidden="true">${flag}</span>
                  <span class="country-selector__option-name">${country.name}</span>
                  <span class="country-selector__option-prefix">${country.dialCode}</span>
                </button>
              </li>
            `;
          }
        ).join("");
      }

      toggleCountryDropdown() {
        if (this.countryDropdown.hidden) {
          this.openCountryDropdown();
          return;
        }

        this.closeCountryDropdown();
      }

      openCountryDropdown() {
        this.countryDropdown.hidden = false;
        this.countryTrigger.setAttribute("aria-expanded", "true");
        this.countryTrigger.classList.add("is-open");
      }

      closeCountryDropdown() {
        this.countryDropdown.hidden = true;
        this.countryTrigger.setAttribute("aria-expanded", "false");
        this.countryTrigger.classList.remove("is-open");
      }

      selectCountry(country) {
        this.countryInput.value = country.dialCode.replace(/(?!^\+)\D+/g, "");
        this.countryInput.dataset.countryCode = country.code;
        this.countryTriggerPlaceholder.hidden = true;
        this.countryTriggerValue.hidden = false;
        this.countryTriggerValue.textContent = `${getCountryFlagEmoji(
          country.code
        )} ${country.dialCode}`;
        this.countryTrigger.classList.remove("is-placeholder");
        this.showCountryValidationState(false);

        this.countryOptions
          .querySelectorAll(".country-selector__option")
          .forEach((option) => {
            const isSelected = option.dataset.countryCode === country.code;
            option.classList.toggle("is-selected", isSelected);
            option.setAttribute("aria-selected", isSelected ? "true" : "false");
          });

        this.closeCountryDropdown();
      }

      updateCountryTrigger() {
        const selectedCountryCode = this.countryInput.dataset.countryCode;
        const selectedCountry = SORTED_ACTIVATE_CODE_COUNTRIES.find(
          ({ code }) => code === selectedCountryCode
        );

        if (!selectedCountry) {
          this.countryTriggerPlaceholder.hidden = false;
          this.countryTriggerValue.hidden = true;
          this.countryTriggerValue.textContent = "";
          this.countryTrigger.classList.add("is-placeholder");
          return;
        }

        this.selectCountry(selectedCountry);
      }

      validateCountrySelection() {
        const hasValue = Boolean(this.countryInput?.value?.trim());
        this.showCountryValidationState(!hasValue);

        if (!hasValue && this.countryTrigger) {
          this.countryTrigger.focus();
        }

        return hasValue;
      }

      showCountryValidationState(isInvalid) {
        if (!this.countrySelector) {
          return;
        }

        this.countrySelector.classList.toggle("is-invalid", isInvalid);
      }
    },
    { extends: "form" }
  );
}

class validateCode extends APIRequestHandler {
  constructor() {
    super();
    this.pdpForm = document.forms[this.getAttribute("data-form-id")];
    this.atcButton = this.pdpForm.querySelector('button[type="submit"]');
    this.submitButton = this.querySelector("button");
    this.confirmed = false;
  }

  connectedCallback() {
    this.addEventListener("submit", this.handleSubmit.bind(this));
    document.addEventListener(
      "pp:update-atc",
      this.handleVariantChange.bind(this)
    );
    this.input = this.querySelector("input");
    this.errorDiv = this.querySelector("[data-error-container]");
  }

  handleVariantChange(e) {
    if (this.confirmed) {
      this.setButtonVisible();
    }
  }

  setButtonVisible() {
    this.atcButton = this.pdpForm.querySelector('button[type="submit"]');
    let buttonToChange =
      this.atcButton.querySelector(".loader-button__text") || this.atcButton;
    buttonToChange.innerText = this.atcButton.getAttribute(
      "data-button-content"
    );

    this.atcButton.removeAttribute("disabled");
    this.input.setAttribute("disabled", true);
    this.atcButton
      .closest("product-payment-container")
      ?.setAttribute("confirmed", true);
    this.confirmed = true;
  }

  async handleSubmit(e) {
    e.preventDefault();
    this.submitButton.classList.remove("success");
    this.errorDiv.classList.remove("error");

    let code = this.input.value;
    await this.makeRequest(
      "GET",
      `/foundCode/checkActiveCode/${code}`,
      (response) => {
        if (response && response.exists && response.valid) {
          this.submitButton.classList.add("success");
          this.submitButton.innerText =
            this.submitButton.getAttribute("data-success");
          this.submitButton.setAttribute("disabled", true);

          let inputElement = document.createElement("input");
          inputElement.type = "hidden";
          inputElement.name = "properties[_code]";
          inputElement.value = code;
          this.pdpForm.appendChild(inputElement);
          this.setButtonVisible();
        } else {
          this.errorDiv.classList.add("error");
          let errorText = this.submitButton.getAttribute("data-error");
          if (response && !response.valid && response.exists) {
            errorText = this.submitButton.getAttribute("data-error-invalid");
          }
          this.errorDiv.innerText = errorText;
          this.submitButton.innerText = this.submitButton.getAttribute(
            "data-error-retry-text"
          );
        }
      }
    );
  }
}

customElements.define("validate-code", validateCode, {
  extends: "form",
});

class FoundCodeUtils {
  static initializeSection(sectionId) {
    const section = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (!section) return;

    const mailOptionBtn = section.querySelector(".mail-option-btn");
    if (mailOptionBtn) {
      mailOptionBtn.addEventListener("click", () => {
        FoundCodeUtils.toggleMailInfo(section);
      });
    }
  }

  static toggleMailInfo(section) {
    const mailInfo = section.querySelector('[data-info="mail"]');

    if (mailInfo.style.display === "none" || !mailInfo.style.display) {
      mailInfo.style.display = "block";
    } else {
      mailInfo.style.display = "none";
    }
  }

  static resetSection(section) {
    const steps = section.querySelectorAll("[data-step]");
    steps.forEach((step, index) => {
      step.style.display = index === 0 ? "block" : "none";
    });

    const forms = section.querySelectorAll("form");
    forms.forEach((form) => form.reset());

    const errorMessages = section.querySelectorAll('[data-message="error"]');
    errorMessages.forEach((msg) => (msg.style.display = "none"));
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const foundCodeSections = document.querySelectorAll(".found-code-section");
  foundCodeSections.forEach((section) => {
    const sectionId = section.getAttribute("data-section-id");
    FoundCodeUtils.initializeSection(sectionId);
  });

  document.addEventListener("variant:added", (e) => {
    if (e.detail?.variant?.properties?._code) {
      window.location.reload();
    }
  });
});
